import { useMutation } from '@tanstack/react-query';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import { useSocket } from '@/components/socket-provider';
import { toast } from '@/components/ui/use-toast';
import { sampleDataApi } from '@/features/flows/lib/sample-data-api';
import {
  AI_BUILDER_SIGNALS,
  AiBuilderSystemMessage,
  AiBuilderFlowPartialUpdate,
  AiBuilderNarration,
  AiBuilderProgress,
  AskAiBuilderResponse,
  GenerateFlowRequest,
  FlowVersion,
  NavigateDirective,
  WebsocketClientEvent,
  flowStructureUtil,
  isNil,
  maskTypedSecrets,
} from '@activepieces/shared';

import { useBuilderStateContext } from '../builder-hooks';

import {
  AiBuilderChatMessage,
  buildHelpMessage,
  hasFlowStructureChanged,
  isAnswerLost,
  toChatContext,
} from './ai-builder-utils';
import { useChatStore } from './useChatStore';

/**
 * The whole turn protocol of the AI builder chat, extracted from the
 * component so it is testable: sending (free text and protocol payloads),
 * the resume token rules, retry, the socket events, and the auto-resume
 * gates. The component keeps only DOM concerns (scroll, focus) and rendering.
 */
export const useAiBuilderTurn = (): {
  sendMessage: (text: string) => Promise<void>;
  sendSilentAction: (
    payload: string,
    displayedChoice?: string,
  ) => Promise<void>;
  /**
   * Replays a failed turn: truncates the failed message from the chat AND
   * re-sends its exact sourcePrompt (protocol payloads via the silent path).
   * Both gestures live here — they are one operation; the component keeps
   * only the Undo button, which is a chat edit, not a turn.
   */
  retryTurn: (message: AiBuilderChatMessage) => void;
  isGenerating: boolean;
  /** The last thing the user said never got its answer (page left mid-turn) */
  isAnswerLost: boolean;
  loadingMessage: string;
} => {
  const {
    flowVersion,
    generateFlow,
    selectStepByName,
    applyAiFlowUpdate,
    applyAiStepState,
    applyAiPartialFlowElements,
    waitForSavesToSettle,
    setHighlightKey,
    setConnectionDialogOpen,
    triggerAutoLayout,
    triggerCanvasRefit,
    aiHelpRequest,
    clearAiHelpRequest,
    sampleData,
    setSampleData,
    connectionCreatedNonce,
  } = useBuilderStateContext((state) => ({
    flowVersion: state.flowVersion,
    generateFlow: state.generateFlow,
    selectStepByName: state.selectStepByName,
    applyAiFlowUpdate: state.applyAiFlowUpdate,
    applyAiStepState: state.applyAiStepState,
    applyAiPartialFlowElements: state.applyAiPartialFlowElements,
    waitForSavesToSettle: state.waitForSavesToSettle,
    setHighlightKey: state.setHighlightKey,
    setConnectionDialogOpen: state.setConnectionDialogOpen,
    triggerAutoLayout: state.triggerAutoLayout,
    triggerCanvasRefit: state.triggerCanvasRefit,
    aiHelpRequest: state.aiHelpRequest,
    clearAiHelpRequest: state.clearAiHelpRequest,
    sampleData: state.sampleData,
    setSampleData: state.setSampleData,
    connectionCreatedNonce: state.connectionCreatedNonce,
  }));

  const flowId = flowVersion.flowId;
  const { chats, turnsInFlight, addMessage, setMessages, startTurn, endTurn } =
    useChatStore();
  const messages = chats[flowId] || [];
  const socket = useSocket();

  // In the store, not in this component: a turn outlives the page that sent
  // it, and the chat reopened meanwhile must still show it running
  const isGenerating = turnsInFlight[flowId] === true;
  const [loadingMessage, setLoadingMessage] = useState<string>(
    t('Thinking...'),
  );
  const previousFlowVersionRef = useRef<FlowVersion | null>(null);
  // Tells a turn this chat sent (its own callbacks apply the answer) from one
  // a previous mount sent (they apply it to that page's builder, not this one)
  const sentTurnRef = useRef(false);
  const agentStateIdRef = useRef<string | undefined>(undefined);
  // The prompt of the turn in flight — stored on its answer so retry can
  // replay exactly that turn
  const sentPromptRef = useRef<string | undefined>(undefined);
  // Armed while the wizard waits on a "connect an account" gate: the matching
  // connection creation resumes the wizard without any user click
  const openAuthPendingRef = useRef(false);

  // The creation name is written TRANSLATED by the client, so the server
  // cannot recognise it: only here can we say "still unnamed"
  const isUnnamedFlow = flowVersion.displayName === t('Untitled');

  // The builder loads sample data once at page mount; a turn that server-tested
  // steps (trigger sampling, auto-advance) changed it behind the panel's back.
  // Only steps carrying a sample file are fetched — never clearing local state.
  const refreshSampleData = async (version: FlowVersion) => {
    const testedSteps = flowStructureUtil.getAllSteps(version).filter(
      (step) =>
        !isNil(
          (
            step.settings as {
              inputUiInfo?: { sampleDataFileId?: string };
            }
          ).inputUiInfo?.sampleDataFileId,
        ),
    );
    await Promise.all(
      testedSteps.map(async (step) => {
        try {
          const payload = await sampleDataApi.get({
            flowId: version.flowId,
            flowVersionId: version.id,
            stepName: step.name,
          });
          setSampleData(step.name, payload);
        } catch {
          // The panel stays stale until reload — not worth failing the turn
        }
      }),
    );
  };

  const applyServerVersion = (
    serverVersion: FlowVersion,
    baselineVersion: FlowVersion | null,
  ) => {
    // Only take the server version when the turn actually changed the flow.
    // On a configure turn it is identical (or staler than) what the user is
    // editing in the panel: applying it resets the form, and the panel's
    // autosave would then persist those reset values.
    const structureChanged =
      !baselineVersion ||
      hasFlowStructureChanged(baselineVersion, serverVersion);
    if (structureChanged) {
      applyAiFlowUpdate(serverVersion);
      triggerAutoLayout();
    } else {
      applyAiStepState(serverVersion, baselineVersion);
    }
    // Even a structure-preserving turn may have tested steps server-side
    void refreshSampleData(serverVersion);
  };

  const followDirectives = (directives: NavigateDirective[]) => {
    // Any fresh answer supersedes a previous connect-account gate
    openAuthPendingRef.current = false;
    const lastDirective = directives[directives.length - 1];
    if (!lastDirective) return;
    if (lastDirective.stepName) {
      selectStepByName(lastDirective.stepName);
    }
    if (lastDirective.propToHighlight) {
      setHighlightKey(lastDirective.propToHighlight);
    }
    if (lastDirective.action === 'OPEN_AUTH') {
      openAuthPendingRef.current = true;
      setConnectionDialogOpen(true);
    }
  };

  const { mutate } = useMutation({
    mutationFn: (params: GenerateFlowRequest) => {
      previousFlowVersionRef.current = structuredClone(flowVersion);
      sentPromptRef.current = params.prompt;
      return generateFlow(params, socket);
    },
    onSuccess: (response: AskAiBuilderResponse) => {
      if (response.flow?.version) {
        applyServerVersion(
          response.flow.version,
          previousFlowVersionRef.current,
        );
      }

      // Keep the resume token only when the server expects a reply; an error
      // payload (flow: null) must not wipe a pending conversation state
      if (response.flow) {
        agentStateIdRef.current = response.waitingForReply
          ? response.agentStateId
          : undefined;
      }

      const botMessage: AiBuilderChatMessage = {
        content: response.message?.content ?? '',
        contentParams: response.message?.contentParams,
        contentPrefix: response.message?.contentPrefix,
        userType: 'bot',
        previousFlowVersion: previousFlowVersionRef.current,
        newFlowVersion: response.flow?.version,
        suggestedActions: response.suggestedActions,
        card: response.card,
        sourcePrompt: sentPromptRef.current,
        flowReady: response.flowReady,
        navigateDirectives: response.navigateDirectives,
      };
      addMessage(flowId, botMessage);
      followDirectives(response.navigateDirectives ?? []);
    },
    onError: (error) => {
      // The turn is lost (client timeout, socket dropped): the toast is gone as
      // soon as the user navigates, so the chat must keep the trace itself —
      // otherwise the conversation looks silently dead on the next visit.
      addMessage(flowId, {
        content: AiBuilderSystemMessage.turnFailed,
        userType: 'bot',
        // What retry replays — this is the one case the button is for
        sourcePrompt: sentPromptRef.current,
      });
      toast({
        variant: 'destructive',
        title: t('Error generating flow'),
        description: error.message,
      });
    },
    onSettled: (_response, _error, params) => {
      endTurn(params.flowId);
    },
  });

  /**
   * Sends one turn against an EXPLICIT history. Callers pass the conversation
   * they just wrote rather than letting this read `messages`: within a turn
   * that closure still holds the state from before their own setMessages.
   */
  const dispatchTurn = async (
    text: string,
    history: AiBuilderChatMessage[],
  ) => {
    sentTurnRef.current = true;
    startTurn(flowId);
    await waitForSavesToSettle();
    mutate({
      prompt: text,
      context: toChatContext(history),
      flowId: flowVersion.flowId,
      agentStateId: agentStateIdRef.current,
      flowIsUnnamed: isUnnamedFlow,
    });
  };

  const sendMessage = async (typed: string) => {
    // A secret typed here would be stored, replayed to the model on every turn
    // and copied into the flow: only its mask is kept and sent
    const text = maskTypedSecrets(typed);
    if (text !== typed) {
      toast({
        title: t('Secret removed from your message'),
        description: t(
          'Paste it into the field of the step, in the panel: everything typed in the chat is kept in the conversation.',
        ),
      });
    }
    const userMessage: AiBuilderChatMessage = {
      content: text,
      userType: 'user',
    };

    addMessage(flowId, userMessage);
    await dispatchTurn(text, [...messages, userMessage]);
  };

  /**
   * Sends a protocol payload ([STEP_DONE], [CARD_REPLY]…) the user never types.
   * `displayedChoice` is what they actually picked — a button label, an option
   * — and it stays in the conversation: without it the select and the buttons
   * vanish with the next message and nobody can tell what was answered.
   */
  const sendSilentAction = async (text: string, displayedChoice?: string) => {
    const answered = displayedChoice
      ? messages.map((message, index) =>
          index === messages.length - 1 && message.userType === 'bot'
            ? { ...message, answeredChoice: displayedChoice }
            : message,
        )
      : messages;
    if (displayedChoice) {
      // The choice replaces the select/buttons under the very message it
      // answers — a validated pick, not a line the user typed
      setMessages(flowId, answered);
    }
    // Panel edits (e.g. the dropdown the wizard just asked for) are autosaved
    // asynchronously: sending before they settle makes the server re-read a
    // stale version and ask the same question again — dispatchTurn waits.
    await dispatchTurn(text, answered);
  };

  const retryTurn = (message: AiBuilderChatMessage): void => {
    // Replays THIS turn: its own prompt, protocol payload included. Anything
    // else re-ran an unrelated request — on a finished flow, the original
    // build one.
    const prompt = message.sourcePrompt;
    if (!prompt) return;
    // Drops the failed answer and replays the turn as it was first sent — its
    // own line is already in the history, whether the user typed it or it was
    // a protocol payload nobody displayed. Re-adding it would print the prompt
    // twice and hand the model a duplicated user turn.
    const withoutFailedAnswer = messages.slice(0, -1);
    setMessages(flowId, withoutFailedAnswer);
    void dispatchTurn(prompt, withoutFailedAnswer);
  };

  // Handle "Need Help?" button from step settings. The ref guards the render
  // between the send and the store clearing the request; once cleared, the
  // same step can ask again. A request made mid-turn waits for the turn to end.
  const helpRequestSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (aiHelpRequest === null) {
      helpRequestSentRef.current = null;
      return;
    }
    if (isGenerating || helpRequestSentRef.current === aiHelpRequest) return;
    helpRequestSentRef.current = aiHelpRequest;
    const helpMessage = buildHelpMessage({
      flowVersion,
      sampleData,
      stepName: aiHelpRequest,
    });
    void sendSilentAction(helpMessage);
    clearAiHelpRequest();
    // flowVersion and sampleData are read when the request is sent: a change
    // of either must not send it again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiHelpRequest, isGenerating]);

  // Auto-resume the wizard once the connection asked by a connect-account
  // gate is created — no "done" click needed
  useEffect(() => {
    if (
      connectionCreatedNonce > 0 &&
      openAuthPendingRef.current &&
      !isGenerating
    ) {
      openAuthPendingRef.current = false;
      void sendSilentAction(AI_BUILDER_SIGNALS.STEP_DONE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionCreatedNonce]);

  useEffect(() => {
    if (!socket) return;

    // One socket per tab: a turn still running for another flow keeps
    // emitting after the tab moved to this flow's chat
    const handleAiBuilderProgress = (data: AiBuilderProgress) => {
      if (data.flowId !== flowId) return;
      setLoadingMessage(t(data.message || 'Thinking...'));
    };

    const handlePartialUpdate = (data: AiBuilderFlowPartialUpdate) => {
      if (data.flowId !== flowId) return;
      // Live canvas preview: local state only — the agent persists server-side,
      // and the store merge reads the CURRENT version (no stale closure).
      // No auto-layout here: it persists a DRAG_STEP per node, and those steps
      // do not exist server-side yet mid-turn (the request 500s, which halts the
      // save queue and silently drops every later edit). Layout runs on the
      // final response instead, once the agent's steps are persisted.
      if (data.flowElements) {
        applyAiPartialFlowElements(data.flowElements);
        triggerCanvasRefit();
      }
    };

    // A mid-turn line the assistant reports on its own (auto-selection, step
    // auto-tested): persisted as a normal bot message, translated by the chat.
    // It goes to its own flow's chat, shown or not: the turn's answer lands
    // there too, and a validation issue line is part of that answer.
    const handleNarration = (payload: AiBuilderNarration) => {
      addMessage(payload.flowId, {
        content: payload.content,
        contentParams: payload.contentParams ?? {},
        userType: 'bot',
        narration: true,
      });
    };

    socket.on(
      WebsocketClientEvent.AI_BUILDER_PROGRESS,
      handleAiBuilderProgress,
    );
    socket.on(
      WebsocketClientEvent.AI_BUILDER_FLOW_PARTIAL_UPDATE,
      handlePartialUpdate,
    );
    socket.on(WebsocketClientEvent.AI_BUILDER_NARRATION, handleNarration);
    return () => {
      socket.off(
        WebsocketClientEvent.AI_BUILDER_PROGRESS,
        handleAiBuilderProgress,
      );
      socket.off(
        WebsocketClientEvent.AI_BUILDER_FLOW_PARTIAL_UPDATE,
        handlePartialUpdate,
      );
      socket.off(WebsocketClientEvent.AI_BUILDER_NARRATION, handleNarration);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, flowId]);

  // A turn sent by a previous mount ends while this chat is open: its own
  // callbacks applied the answer to the page that sent it, and this page still
  // shows the flow as it loaded it, mid-turn, with nothing selected or opened
  const wasGeneratingRef = useRef(isGenerating);
  useEffect(() => {
    const turnJustEnded = wasGeneratingRef.current && !isGenerating;
    wasGeneratingRef.current = isGenerating;
    if (!turnJustEnded) return;
    if (sentTurnRef.current) {
      sentTurnRef.current = false;
      return;
    }
    const answer = messages[messages.length - 1];
    if (answer?.userType !== 'bot') return;
    if (answer.newFlowVersion) {
      applyServerVersion(
        answer.newFlowVersion,
        answer.previousFlowVersion ?? null,
      );
    }
    followDirectives(answer.navigateDirectives ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerating]);

  useEffect(() => {
    const currentMessages = useChatStore.getState().chats[flowId];
    if (
      flowId &&
      (!currentMessages || currentMessages.length === 0) &&
      !helpRequestSentRef.current
    ) {
      addMessage(flowId, {
        content: `${t('Welcome to AI Builder!')}\n${t(
          "Could you please describe the desired flow or any modifications you'd like to implement?",
        )}`,
        userType: 'bot',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, addMessage, t]);

  return {
    sendMessage,
    sendSilentAction,
    retryTurn,
    isGenerating,
    isAnswerLost: isAnswerLost(messages, isGenerating),
    loadingMessage,
  };
};
